/**
 * IMPORTS
 */
import {setTagColor} from 'src/aggregates/preferences/api';
import * as preferencesSelectors from 'src/aggregates/preferences/selectors';
import * as events from 'src/aggregates/tags/events';
import {DEFAULT_COLOR} from 'src/aggregates/tags/selectors';
import {createTag} from 'src/aggregates/tags/utils';
import {loadTags} from 'src/aggregates/tags/utils';
import * as userSelectors from 'src/aggregates/user/selectors';
import {updateTag} from 'src/aggregates/tags/utils';


/**
 * TYPES
 */
import {IAppState} from 'src/aggregates/index.d';
import {IEvent} from 'src/aggregates/index.d';
import * as Commands from 'src/aggregates/tags/commands.d';
import {IReducedGraphisTag} from 'src/aggregates/tags/handlers.d';


/**
 * CODE
 */

/**
 * I am a tag create command handler.
 *
 * :param command: create command
 * :param state: application state
 *
 * :generates: events
 */
async function* create (
    command: Commands.ITagsCreate,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate tags loading event
    yield events.tagCreating();

    // get logged agent company
    const company = userSelectors.fetch().company;

    // load tags
    const tag = await createTag(command.name, company);

    // tags load failed: generate tags not loaded event
    if (tag === null)
    {
        yield events.tagNotCreated();
        return;
    }

    // tags loaded succesfully: generate tags loaded event
    yield events.tagCreated(tag.company, tag.id, tag.name, command.color);
    setTagColor(tag.id, command.color);
}


/**
 * I am a tags load command handler.
 *
 * :param command: load command
 * :param state: application state
 *
 * :generates: events
 */
async function* load (
    command: Commands.ITagsLoad,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate tags loading event
    yield events.tagLoading();

    // get logged agent company
    const company = userSelectors.fetch().company;

    // load tags
    const tags = await loadTags(company);

    // tags load failed: generate tags not loaded event
    if (tags === null)
    {
        yield events.tagNotLoaded();
        return;
    }

    // get logged user
    const user = Number(state.user.id);

    // set tag colors
    const reducedTags: IReducedGraphisTag[] = tags.map((tag) =>
    {
        // get tag color
        let color = preferencesSelectors.getTagColor(tag.id, user);

        // color not found: set color as default
        if (color === null)
        {
            color = DEFAULT_COLOR;
        }

        // return reduced tag
        return {
            ...tag,
            color,
        };
    });

    // tags loaded succesfully: generate tags loaded event
    yield events.tagLoaded(reducedTags);
}


/**
 * I am a tags reset error command handler.
 *
 * :param command: reset error command
 * :param state: application state
 *
 * :generates: events
 */
async function* resetError (
    command: Commands.ITagsErrorReset,
    state: IAppState,
): AsyncIterable<IEvent>
{
    yield events.tagsErrorReset();
}


/**
 * I am a tags update command handler.
 *
 * :param command: load command
 * :param state: application state
 *
 * :generates: events
 */
async function* update (
    command: Commands.ITagsUpdate,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate tag updating event
    yield events.tagUpdating();

    // load tags
    const tag = await updateTag(command._target, command.tag);

    // tags load failed: generate tag not updated event
    if (tag === null)
    {
        yield events.tagNotUpdated();
        return;
    }

    // set tag color
    const reducedTag: IReducedGraphisTag = {
        ...tag,
        color: command.color,
    };

    // tags loaded succesfully: generate tag updated event
    yield events.tagUpdated(reducedTag);
    setTagColor(command._target, command.color);
}


/**
 * EXPORTS
 */
export {
    create,
    load,
    resetError,
    update,
};
